import { useMemo, useEffect, useState } from 'react';
import { SelectField } from 'components/FormFields';
import { components, OptionProps, SingleValueProps } from 'react-select';
import Render from 'components/Common/Render';
import { useCountryExtensionsQuery } from 'store/apis/countryExtensionApi';
import { getCountryCallingCode, CountryCode } from 'libphonenumber-js';

export type CountryOption = {
  value: string;
  label: string;
  flag?: string;
  code: string;
  name: string;
  callingCode: string;
};

interface PhoneInputWithFlagProps {
  defaultCountryCode?: string;
  initialPhoneCountryExtension?: string;
  phoneNumber: string;
  onPhoneChange: (value: string) => void;
  onCountryChange: (option: CountryOption) => void;
  error?: string | false | null;
  touched?: boolean;
  disabled?: boolean;
}

const getCallingCode = (code: string): string => {
  try {
    return `+${getCountryCallingCode(code as CountryCode)}`;
  } catch {
    return '+1';
  }
};

const CustomOption = (props: OptionProps<CountryOption, false>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <Render when={!!props.data.flag}>
        <span className="text-xl" role="img" aria-label={props.data.name}>
          {props.data.flag}
        </span>
      </Render>
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const CustomSingleValue = (props: SingleValueProps<CountryOption, false>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center justify-center w-full h-full">
      <Render when={!!props.data.flag}>
        <span
          className="inline-flex items-center justify-center text-[28px] leading-none w-[28px] h-[28px]"
          role="img"
          aria-label={props.data.name}
        >
          {props.data.flag}
        </span>
      </Render>
    </div>
  </components.SingleValue>
);

const PhoneInputWithFlag: React.FC<PhoneInputWithFlagProps> = ({
  defaultCountryCode = 'US',
  initialPhoneCountryExtension,
  phoneNumber,
  onPhoneChange,
  onCountryChange,
  error,
  touched,
  disabled = false,
}) => {
  const { data: countryExtensions } = useCountryExtensionsQuery();

  const countryOptions: CountryOption[] = useMemo(() => {
    if (!countryExtensions || countryExtensions.length === 0) {
      return [
        {
          value: '+1',
          label: 'US +1',
          flag: '🇺🇸',
          code: 'US',
          name: 'United States',
          callingCode: '+1',
        },
      ];
    }
    const options = countryExtensions.map((country) => ({
      value: country.dialCode,
      label: `${country.code} ${country.dialCode}`,
      flag: country.flag,
      code: country.code,
      name: country.name,
      callingCode: country.dialCode,
    }));
    return options.sort((a, b) => {
      if (a.code === 'US') return -1;
      if (b.code === 'US') return 1;
      return 0;
    });
  }, [countryExtensions]);

  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);

  useEffect(() => {
    if (countryOptions.length > 0 && !selectedCountry) {
      const defaultOption = countryOptions.find((c) => c.code === defaultCountryCode);
      if (defaultOption) {
        setSelectedCountry(defaultOption);
      }
    }
  }, [countryOptions, defaultCountryCode, selectedCountry]);

  const phoneCountryExtension = useMemo(() => {
    if (initialPhoneCountryExtension && !selectedCountry) return initialPhoneCountryExtension;
    return selectedCountry?.callingCode || getCallingCode(defaultCountryCode);
  }, [initialPhoneCountryExtension, selectedCountry, defaultCountryCode]);

  const handleCountryChange = (option: CountryOption | null) => {
    if (option) {
      setSelectedCountry(option);
      onCountryChange(option);
    }
  };

  const displayPhoneNumber = useMemo(() => {
    if (!phoneNumber) return '';
    const extensionDigits = phoneCountryExtension?.replace(/\D/g, '') || '';
    if (phoneNumber.startsWith('+') && extensionDigits && phoneNumber.replace(/\D/g, '').startsWith(extensionDigits)) {
      return phoneNumber.replace(/\D/g, '').slice(extensionDigits.length);
    }
    if (extensionDigits && phoneNumber.startsWith(extensionDigits)) {
      return phoneNumber.slice(extensionDigits.length);
    }
    return phoneNumber;
  }, [phoneNumber, phoneCountryExtension]);

  return (
    <div className="flex flex-col gap-2 mt-2 pb-1 pr-1">
      <label className="font-poppins font-normal text-[12px] leading-[100%] text-darkBlack">Phone Number</label>
      <div className="flex">
        {/* Flag dropdown */}
        <div
          className={`w-[92px] h-[44px] flex-shrink-0 flex items-center justify-between px-3 rounded-tl-[4px] rounded-bl-[4px] border-t border-b border-l border-r-[0.5px] border-borderGrey ${disabled ? 'bg-offWhiteBg' : 'bg-white'}`}
        >
          <SelectField<CountryOption>
            value={selectedCountry}
            options={countryOptions}
            onChange={handleCountryChange}
            isSearchable={!disabled}
            isDisabled={disabled}
            placeholder="🌍"
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={document.body}
            components={{
              Option: CustomOption,
              SingleValue: CustomSingleValue,
              IndicatorSeparator: () => null,
            }}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '42px',
                height: '42px',
                border: 'none',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                cursor: disabled ? 'default' : 'pointer',
              }),
              dropdownIndicator: (base) => ({
                ...base,
                padding: '0 2px',
              }),
              singleValue: (base) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                lineHeight: '1',
                margin: '0',
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '0 2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              menu: (base) => ({
                ...base,
                minWidth: '250px',
                maxHeight: '300px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid #d1d5db',
                marginTop: '4px',
              }),
              menuList: (base) => ({
                ...base,
                maxHeight: '300px',
                padding: '4px 0',
              }),
              option: (base, state) => ({
                ...base,
                fontSize: '14px',
                fontFamily: 'Poppins',
                padding: '8px 12px',
                backgroundColor: state.isSelected
                  ? '#6B46C1'
                  : state.isFocused
                    ? '#f3f4f6'
                    : 'white',
                color: state.isSelected ? 'white' : '#1f2937',
                cursor: 'pointer',
              }),
            }}
          />
        </div>
        {/* Phone number input */}
        <div
          className={`flex-1 min-w-0 relative h-[44px] rounded-tr-[4px] rounded-br-[4px] border-t border-b border-r border-l-[0.5px] border-borderGrey ${disabled ? 'bg-offWhiteBg' : 'bg-white'}`}
        >
          <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-700 font-medium font-poppins text-sm z-10">
            {phoneCountryExtension}&nbsp;
          </span>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="Enter number"
            onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ''))}
            value={displayPhoneNumber}
            disabled={disabled}
            className="w-full h-full pr-5 bg-transparent focus:outline-none font-poppins text-sm placeholder:text-silverGray"
            style={{
              paddingLeft: `${(phoneCountryExtension?.length || 2) * 9 + 28}px`,
            }}
          />
        </div>
      </div>
      {touched && error && <span className="text-red-500 text-xs">{error}</span>}
    </div>
  );
};

export default PhoneInputWithFlag;
